import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, Home, Package, User, Search, Bell, MapPin, Menu, Heart, Gift, Phone, MessageSquare, Check, Mail, Chrome, Eye, EyeOff, Camera, Shield, Fingerprint, Key, Smartphone, Clock, Monitor, Activity, AlertTriangle, LogOut, Settings, ChevronRight, Truck, XCircle, CheckCircle, Landmark } from 'lucide-react';
import { products,banners, locations, notifications, subscriptionPlans } from './data/products';
import { authAPI, productsAPI, cartAPI, ordersAPI } from './services/api';

import HomeScreen from './components/HomeScreen';
import CartScreen from './components/CartScreen';
import OrdersScreen from './components/OrdersScreen';
import ProfileScreen from './components/ProfileScreen';
import HelpSupportScreen from './components/HelpSupportScreen';
import SecuritySettingsModal from './components/modals/SecuritySettingsModal';
import TwoFactorSetupModal from './components/modals/TwoFactorSetupModal';
import AuthModal from './components/modals/AuthModal';
import CheckoutModal from './components/modals/CheckoutModal';
import SubscriptionModal from './components/modals/SubscriptionModal';
import LocationModal from './components/modals/LocationModal';
import MyOrdersModal from './components/modals/MyOrdersModal';
import FavoritesModal from './components/modals/FavoritesModal';
import NotificationsModal from './components/modals/NotificationsModal';
import PaymentMethodsModal from './components/modals/PaymentMethodsModal';
import EditAddressModal from './components/modals/EditAddressModal';
import EditProfileModal from './components/modals/EditProfileModal';
import ForgotPasswordModal from './components/modals/ForgotPasswordModal';
import SignUpModal from './components/modals/SignUpModal';
import PaymentModal from './components/modals/PaymentModal';
import OrderConfirmationModal from './components/modals/OrderConfirmationModal';
import Dashboard from './Dashboard';
import apiService from './apiService';
import AddAddressModal from './components/modals/AddAddressModal';
import * as cartHelpers from './utils/cartHelpers';
import orderHelpers from './utils/orderHelpers';

// ============================================
// Main App Component
const MilkEcommerceApp = () => {

  // State Management
  const [activeTab, setActiveTab] = useState('home');
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [showSubscription, setShowSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [deliverySlot, setDeliverySlot] = useState('morning');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [currentBanner, setCurrentBanner] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('Gurugram, Haryana');
  const [isAuthenticated, setIsAuthenticated] = useState(!!apiService.getToken());
  const [showAuthModal, setShowAuthModal] = useState(!apiService.getToken());
  const [authStep, setAuthStep] = useState('phone');
  const [mobile, setMobile] = useState('');
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpMethod, setOtpMethod] = useState('sms');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    address: '',
    mobile: '',
    city: '',
    pincode: '',
    profilePic: null,
    whatsappConnected: false  // Add this line
  });

  // Add after userProfile state (around line 62)
  const [deliveryAddresses, setDeliveryAddresses] = useState([
    {
      id: 1,
      label: 'Home',
      recipient_name: userProfile.name || 'John Doe',
      address: userProfile.address || '123 Main Street, Sector 29',
      city: userProfile.city || 'Gurugram',
      state: 'Haryana',
      pincode: userProfile.pincode || '122001',
      country: 'India',
      isDefault: true,
      phone: userProfile.mobile || mobile,
      landmark: 'Near City Mall',
      latitude: 28.4595,
      longitude: 77.0266
    }
  ]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null); 
  const [loginMethod, setLoginMethod] = useState('phone');
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState(['', '', '', '', '', '']);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(1800);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showSecuritySettings, setShowSecuritySettings] = useState(false);
  const [loginHistory, setLoginHistory] = useState([
    { id: 1, device: 'Chrome on Windows', location: 'Gurugram, India', ip: '103.xxx.xxx.123', time: '2 hours ago', current: true },
    { id: 2, device: 'Safari on iPhone', location: 'Delhi, India', ip: '103.xxx.xxx.456', time: '1 day ago', current: false },
    { id: 3, device: 'Chrome on Android', location: 'Noida, India', ip: '103.xxx.xxx.789', time: '3 days ago', current: false }
  ]);
  const [securityLogs, setSecurityLogs] = useState([
    { id: 1, action: 'Login Successful', detail: 'Logged in via Email', time: '2 hours ago', type: 'success' },
    { id: 2, action: '2FA Enabled', detail: 'Two-factor authentication activated', time: '2 hours ago', type: 'info' },
    { id: 3, action: 'Profile Updated', detail: 'Changed delivery address', time: '1 day ago', type: 'info' },
    { id: 4, action: 'Failed Login Attempt', detail: 'Invalid password entered', time: '3 days ago', type: 'warning' },
    { id: 5, action: 'Password Changed', detail: 'Password updated successfully', time: '1 week ago', type: 'success' }
  ]);
    
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([
  { id: 1, type: 'UPI', details: 'milkuser@upi', default: true },
  { id: 2, type: 'Card', details: '**** **** **** 1234', default: false }
]);

const [notifications, setNotifications] = useState([
  { id: 1, title: 'Order Delivered', message: 'Your order #1235 has been delivered', time: '2 hours ago', unread: true, type: 'order' },
  { id: 2, title: 'Special Offer', message: 'Get 20% off on your next subscription', time: '1 day ago', unread: true, type: 'offer' },
  { id: 3, title: 'Payment Successful', message: 'Payment of ₹190 received', time: '2 days ago', unread: false, type: 'payment' },
  { id: 4, title: 'Subscription Reminder', message: 'Your subscription renews tomorrow', time: '3 days ago', unread: false, type: 'reminder' }
]);

  const [productsList, setProductsList] = useState(products);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  // Effects

 // Load products on mount
  useEffect(() => {
    loadProducts();
   // loadCart();
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check if user has a valid token
    const token = apiService.getToken();
    const savedUser = apiService.getUser();
    
    if (token && savedUser) {
      try {
        // Validate token with backend
        const response = await apiService.getAuthenticatedUser();
        
        if (response.success && response.data.user) {
          // Restore user session
          const user = response.data.user;
          setIsAuthenticated(true);
          setShowAuthModal(false);
          setUserProfile({
            name: user.name || '',
            email: user.email || '',
            mobile: user.mobile || '',
            address: user.address || '',
            city: user.city || '',
            pincode: user.pincode || '',
            profilePic: user.profilePic || null
          });
          setMobile(user.mobile || '');
          
          // Load user's addresses (cart will be loaded by useEffect when isAuthenticated changes)
          loadDeliveryAddresses();
          
          console.log('Session restored:', user.name);
        } else {
          // Token invalid, clear session
          apiService.removeToken();
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Session validation failed:', error);
        // Keep local user data but mark as potentially stale
        if (savedUser) {
          setIsAuthenticated(true);
          setShowAuthModal(false);
          setUserProfile({
            name: savedUser.name || '',
            email: savedUser.email || '',
            mobile: savedUser.mobile || '',
            address: savedUser.address || '',
            city: savedUser.city || '',
            pincode: savedUser.pincode || '',
            profilePic: savedUser.profilePic || null
          });
          setMobile(savedUser.mobile || '');
        }
      }
    } else {
      // No token or user data, ensure logged out
      setIsAuthenticated(false);
      setShowAuthModal(true);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getAll();
      const productsData = data.products || [];
      // Use API data if available, otherwise keep existing products
      setProductsList(Array.isArray(productsData) && productsData.length > 0 ? productsData : products);
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
      // Fallback to static products data instead of empty array
      setProductsList(products);
    } finally {
      setLoading(false);
    }
  };

  const loadCart_old = () => {
    const cartData = cartAPI.get();
    setCart(cartData);
  };

 const loadCart = async () => {
  try {
    const cartData = await cartHelpers.loadCart();
  //  alert("Loaded Cart Data:"+JSON.stringify(cartData));
    // Ensure it's always an array
    setCart(Array.isArray(cartData) ? cartData : []);
  } catch (error) {
    console.error('Error loading cart:', error);
    // Fallback to empty cart
    setCart([]);
  }
};
  const handleLogin = async (email, password) => {
    try {
      setLoading(true);
      const data = await authAPI.login(email, password);
      setIsAuthenticated(true);
      console.log(`Welcome ${data.user.name}!`);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTPForLogin = async (mobile, password, method) => {
  try {
    setLoading(true);
    const response = await apiService.loginWithMobile({
      mobile: mobile,
      password: password,
      remember: true
    });

    if (response.success) {
      setIsAuthenticated(true);
      return { success: true, data: response }; // Return success
    } else {
      console.log('Login failed: ' + response.data.message);
      return { success: false, error: response.data.message }; // Return failure
    }    
  } catch (err) {
    console.log('Error: '+err.message);
    return { success: false, error: err.message }; // Return error
  } finally {
    setLoading(false);
  }
};


  const handleSendOTP = async (mobile, method) => {
    try {
      setLoading(true);
     // await authAPI.sendOTP(mobile, method);
      const response = await apiService.sendOTPForLogin(mobile);
      console.log(`OTP sent via ${method}`);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (mobile, otp) => {
    try {
      setLoading(true);
      //const data = await authAPI.verifyOTP(mobile, otp);
      const response = await apiService.verifyMobileOtp(mobile, otp);
      setIsAuthenticated(true);
      console.log('Login successful!');
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart_old = (product) => {
    const updatedCart = cartAPI.add(product);
    setCart(updatedCart);
    console.log(`${product.name} added to cart!`);
  };
  
  const handleAddToCart = async (product) => {
    try {
      const updatedCart = await cartHelpers.addToCart(cart, product);
      setCart(updatedCart);
      console.log(`${product.name} added to cart!`);
    } catch (error) {
      console.error('Error adding to cart:', error);
      console.log('Failed to add to cart. Please try again.');
    }
  };
  const handleUpdateQuantity_old = (productId, quantity) => {
    const updatedCart = cartAPI.updateQuantity(productId, quantity);
    setCart(updatedCart);
  };

  const handleRemoveFromCart_old = (productId) => {
    const updatedCart = cartAPI.remove(productId);
    setCart(updatedCart);
  };

  const handleUpdateQuantity = async (productId, quantity) => {
    try {
      // Calculate delta from current quantity
      const item = cart.find(i => i.id === productId);
      if (item) {
        const delta = quantity - item.quantity;
        const updatedCart = await cartHelpers.updateQuantity(cart, productId, delta);
        setCart(updatedCart);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      console.log('Failed to update quantity. Please try again.');
    }
  };

  const handleRemoveFromCart = async (productId) => {
    try {
      const updatedCart = await cartHelpers.removeFromCart(cart, productId);
      setCart(updatedCart);
    } catch (error) {
      console.error('Error removing from cart:', error);
      console.log('Failed to remove item. Please try again.');
    }
  };

  const handleCheckout = async (orderData) => {
    try {
      setLoading(true);
      const order = await ordersAPI.create(orderData);
      cartAPI.clear();
      setCart([]);
      console.log('Order placed successfully!');
      return order;
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open checkout modal
  const openCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty. Please add items to checkout.');
      return;
    }
    setShowCheckout(true);
  };

  const handleCheckout_new = async (orderData) => {
    try {
      setLoading(true);
      const order = await ordersAPI.create(orderData);
      
      // Clear cart using helper
      await clearCart();
      setCart([]);
      
      console.log('Order placed successfully!');
      return order;
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (authStep === 'otp' && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [authStep, timer]);

  useEffect(() => {
    const isAvailable = window.PublicKeyCredential !== undefined;
    setBiometricAvailable(isAvailable);
  }, []);

  // Helper Functions

const addToCart = async (product) => {
  try {
    // Ensure cart is an array
    const currentCart = Array.isArray(cart) ? cart : [];
    const updatedCart = await cartHelpers.addToCart(currentCart, product);
    setCart(updatedCart);
    console.log(`${product.name} added to cart!`);
  } catch (error) {
    console.error('Error adding to cart:', error);
    console.log('Failed to add to cart. Please try again.');
  }
};

  const addToCart_old = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateQuantity_old = (id, delta) => {
    setCart(cart.map(item =>
      item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
    ).filter(item => item.quantity > 0));
  };

const updateQuantity = async (id, delta) => {
  try {
    // Ensure cart is an array
    const currentCart = Array.isArray(cart) ? cart : [];
    const updatedCart = await cartHelpers.updateQuantity(currentCart, id, delta);
    setCart(updatedCart);
  } catch (error) {
    console.error('Error updating quantity:', error);
    console.log('Failed to update quantity. Please try again.');
  }
};

  const removeFromCart_old = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

const removeFromCart = async (id) => {
  try {
    // Ensure cart is an array
    const currentCart = Array.isArray(cart) ? cart : [];
    const updatedCart = await cartHelpers.removeFromCart(currentCart, id);
    setCart(updatedCart);
  } catch (error) {
    console.error('Error removing from cart:', error);
    console.log('Failed to remove item. Please try again.');
  }
};

  const getCartTotal_old = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getCartTotal = () => {
    if (!Array.isArray(cart)) return 0;
    return cartHelpers.getCartTotal(cart);
  };


  const getCartCount_old = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getCartCount = () => {
    if (!Array.isArray(cart)) return 0;
    return cartHelpers.getCartCount(cart);
};

  const clearCart = async () => {
    try {
      await cartHelpers.clearCart();
      setCart([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };
  const deliverySlots = [
    { id: 'morning', time: '6:00 AM - 8:00 AM', icon: '🌅', label: 'Morning' },
    { id: 'afternoon', time: '2:00 PM - 4:00 PM', icon: '☀️', label: 'Afternoon' },
    { id: 'evening', time: '6:00 PM - 8:00 PM', icon: '🌆', label: 'Evening' }
  ];


  const processPayment_old = () => {
    console.log(`Payment processed via ${paymentMethod.toUpperCase()}!\nDelivery scheduled for ${deliverySlots.find(s => s.id === deliverySlot)?.label} slot.\nOrder confirmed!`);
    setCart([]);
    setShowCheckout(false);
    setActiveTab('orders');
  };

  const processPayment = () => {
    // Close checkout and open payment modal
    setShowCheckout(false);
    setShowPayment(true);
  };

  const handlePaymentComplete = async () => {
    try {
      console.log(`Payment processed via ${paymentMethod.toUpperCase()}!\nDelivery scheduled for ${deliverySlots.find(s => s.id === deliverySlot)?.label} slot.\nOrder confirmed!`);
      
      // Ensure cart is an array
      const cartItems = Array.isArray(cart) ? cart : [];
      
      const orderData = {
        deliveryAddressesId: deliveryAddresses.find(addr => addr.isDefault)?.id || deliveryAddresses[0]?.id,
        items: cartItems,
        totalAmount: getCartTotal(),
        paymentMethod: paymentMethod,
        deliverySlot: deliverySlot
      };
      
 
      const response = await orderHelpers.placeOrder(orderData);
      console.log('Order Response:', response);
      if (response.success && response.order.id) {
        console.log('Order ID:', response.order.id);
        
        // Get default delivery address
        const defaultAddress = deliveryAddresses.find(addr => addr.isDefault) || deliveryAddresses[0];
        
        // Prepare order details for confirmation screen
        const confirmationDetails = {
          orderId: response.id,
          items: cartItems.map(item => ({
            ...item,
            icon: item.icon || '🥛'
          })),
          totalAmount: getCartTotal(),
          deliverySlot: deliverySlot,
          paymentMethod: paymentMethod,
          deliveryAddress: defaultAddress,
          estimatedDelivery: 'Tomorrow, ' + new Date(Date.now() + 86400000).toLocaleDateString('en-IN', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          })
        };
        
        setOrderDetails(confirmationDetails);
        
        // Clear cart
        await clearCart();
        setCart([]);
        
        // Close payment modal and show order confirmation
        setShowPayment(false);
        setShowOrderConfirmation(true);
        setShowCheckout(false);
        setActiveTab('home');
       
      } else {
        console.log('Order placed but no order ID returned.');
        setShowPayment(false);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      console.log('Payment processed but failed to clear cart. Please refresh the page.');
      setShowPayment(false);
    }
  };

  const subscribeProduct = (product, plan) => {
    setSubscription({
      product,
      plan,
      startDate: new Date().toLocaleDateString(),
      deliverySlot: 'morning',
      status: 'active'
    });
    console.log(`Subscription activated for ${product.name}!\nPlan: ${plan.name}\nSaving ${plan.discount}% on every delivery.`);
    setShowSubscription(false);
  };

  const sendOTPForLogin = async (method) => {   
  if (mobile.length === 10) {
    const result = await handleSendOTPForLogin(mobile, password, method);
    
    if (result.success) {
      handleSendOTP(mobile, method);
      setOtpMethod(method);
      setAuthStep('otp');
      setTimer(600);
      setCanResend(false);
      const methodText = method === 'sms' ? 'SMS' : 'WhatsApp';
      console.log(`OTP sent via ${methodText} to ${mobile}\n\nDemo OTP: 123456`);
    }
    // Error already shown in handleSendOTPForLogin
  } else {
    console.log('Please enter a valid 10-digit phone number');
  }
};

  const sendOTP = (method) => {
    if (mobile.length === 10) {
      handleSendOTP(mobile, method);
      setOtpMethod(method);
      setAuthStep('otp');
      setTimer(120);
      setCanResend(false);
      const methodText = method === 'sms' ? 'SMS' : 'WhatsApp';
      console.log(`OTP sent via ${methodText} to ${mobile}\n\nDemo OTP: 123456`);
    } else {
      console.log('Please enter a valid 10-digit phone number');
    }
  };

  const resendOTP = () => {
    setTimer(120);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    console.log(`OTP resent via ${otpMethod === 'sms' ? 'SMS' : 'WhatsApp'} to ${mobile}\n\nDemo OTP: 123456`);
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const verifyOTP = async () => {
  const enteredOtp = otp.join('');
  
  if (enteredOtp.length === 6) {
    try {
      setLoading(true);
      const response = await apiService.verifyMobileOtp(mobile, enteredOtp);
      
      if (response.success) {
        console.log('✅ OTP verified successfully!');
        
        // Store token and user data
        apiService.setToken(response.data.token);
        apiService.setUser(response.data.user);
        
        // Sync local cart with backend after login
       // loadCart(); // Ensure cart is loaded
       /* if (cart.length > 0) {
          try {
            const syncedCart = await cartHelpers.syncCartWithBackend(cart);
            setCart(syncedCart);
            console.log('Cart synced with backend after login');
          } catch (syncError) {
            console.error('Failed to sync cart:', syncError);
            // Continue with login even if cart sync fails
          }
        }
          */
        console.log('User data: '+JSON.stringify(response.data.user));
        console.log('Token: '+response.data.token);
        
        // Check if user has existing profile
        if (response.data.user && response.data.user.name && response.data.user.whatsapp_enabled) {
          // Existing user - log them in directly
          setUserProfile({
            name: response.data.user.name,
            email: response.data.user.email || '',
            address: response.data.user.address || '',
            mobile: response.data.user.mobile || '',
            city: response.data.user.city || '',
            pincode: response.data.user.pincode || '',
            profilePic: response.data.user.profilePic || null
          });
          setIsAuthenticated(true);
          setShowAuthModal(false);
          console.log(`Welcome back, ${response.data.user.name}!`);
          addSecurityLog('Login Successful', 'Logged in via Mobile OTP', 'success');
          setAuthStep('completed');
        } else {
          // New user - ask for WhatsApp connection first
          setAuthStep('whatsapp');
        }
      } else {
        console.log('OTP verification failed: ' + (response.data?.message || response.message));
      }
    } catch (err) {
      console.log('Error verifying OTP: ' + err.message);
    } finally {
      setLoading(false);
    }
  } else {
    console.log('Please enter a valid 6-digit OTP');
  }
};

  const verifyOTP_old = async () => {
        const enteredOtp = otp.join('');
        
        if (enteredOtp.length === 6) {
          try {
            setLoading(true);
            const response = await apiService.verifyMobileOtp(mobile, enteredOtp);
            
            if (response.success) {
              // Don't set isAuthenticated yet - let user complete profile first
              console.log('✅ OTP verified successfully!');
              console.log('User data: '+JSON.stringify(response.data.user));
              console.log('Token: '+response.data.token);
              // Store token and user data
              apiService.setToken(response.data.token);
              apiService.setUser(response.data.user);
              console.log('Checking user profile...'+response.data.user.whatsapp_enabled);
              // Check if user has existing profile
              if (response.data.user && response.data.user.name && response.data.user.whatsapp_enabled) {
                // Existing user - log them in directly
                setUserProfile({
                  name: response.data.user.name,
                  email: response.data.user.email || '',
                  address: response.data.user.address || '',
                  mobile: response.data.user.mobile || '',
                  city: response.data.user.city || '',
                  pincode: response.data.user.pincode || '',
                  profilePic: response.data.user.profilePic || null
                });
                setIsAuthenticated(true);
                setShowAuthModal(false);
                console.log(`Welcome back, ${response.data.user.name}!`);
                addSecurityLog('Login Successful', 'Logged in via Mobile OTP', 'success');
                setAuthStep('completed');
               // setActiveTab('home');
              } else {
                // New user - ask for WhatsApp connection first
                setAuthStep('whatsapp');
              }
            } else {
              console.log('OTP verification failed: ' + (response.data?.message || response.message));
            }
          } catch (err) {
            console.log('Error verifying OTP: ' + err.message);
          } finally {
            setLoading(false);
          }
        } else {
          console.log('Please enter a valid 6-digit OTP');
        }
  };

  const connectWhatsApp = async () => {
      try {
        setLoading(true);
        
        // Call API to save WhatsApp preference
        const response = await apiService.connectWhatsApp(whatsAppNumber);
        
        if (response.success) {
          // Update user profile with WhatsApp status
          setUserProfile({ 
            ...userProfile, 
            whatsappConnected: true 
          });
          setUserProfile({
                  name: response.data.user.name,
                  email: response.data.user.email || '',
                  address: response.data.user.address || '',
                  mobile: response.data.user.mobile || '',
                  city: response.data.user.city || '',
                  pincode: response.data.user.pincode || '',
                  profilePic: response.data.user.profilePic || null,  
                  whatsappConnected: true 
          });
          // Log the security event
          addSecurityLog('WhatsApp Connected', `WhatsApp notifications enabled for ${mobile}`, 'success');
          
          // Move to profile completion step
          setAuthStep('profile');
          
          console.log('✅ WhatsApp connected successfully!\n\nYou will receive order updates on WhatsApp.');
        } else {
          console.log('Failed to connect WhatsApp: ' + (response.data?.message || 'Unknown error'));
          // Still proceed to profile on error
          setAuthStep('profile');
        }
      } catch (err) {
        console.error('WhatsApp connection error:', err);
        console.log('Error connecting WhatsApp. You can enable it later from Settings.');
        // Still proceed to profile on error
        setAuthStep('profile');
      } finally {
        setLoading(false);
      }
    };

  const skipWhatsApp = () => {
    // Log that user skipped WhatsApp
    addSecurityLog('WhatsApp Skipped', 'User chose to skip WhatsApp connection', 'info');
    setAuthStep('profile');
  };

  const updateWhatsAppPreference = async (enabled) => {
    try {
      setLoading(true);
      
      const response = await apiService.updateWhatsAppPreference(enabled ? whatsAppNumber : null, enabled);
      
      if (response.success) {
        setUserProfile({ 
          ...userProfile, 
          whatsappConnected: enabled 
        });
        
        addSecurityLog(
          enabled ? 'WhatsApp Enabled' : 'WhatsApp Disabled', 
          `User ${enabled ? 'enabled' : 'disabled'} WhatsApp notifications`, 
          'info'
        );
        
        console.log(`✅ WhatsApp notifications ${enabled ? 'enabled' : 'disabled'} successfully!`);
      } else {
        console.log('Failed to update WhatsApp preference: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error updating WhatsApp preference:', err);
      console.log('Error updating WhatsApp preference. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (pwd) => {
    setPassword(pwd);
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
    
    const strengths = [
      { score: 0, label: '', color: '' },
      { score: 1, label: 'Very Weak', color: 'bg-red-500' },
      { score: 2, label: 'Weak', color: 'bg-orange-500' },
      { score: 3, label: 'Fair', color: 'bg-yellow-500' },
      { score: 4, label: 'Good', color: 'bg-blue-500' },
      { score: 5, label: 'Strong', color: 'bg-green-500' }
    ];
    
    setPasswordStrength(strengths[score]);
  };

  const handleEmailLogin = (email, pwd) => {
    if (email && pwd) {
      console.log(`✅ Logging in with email...\n\nEmail: ${email}`);
      setUserProfile({ ...userProfile, email });
      setAuthStep('profile');
      setLoginMethod('email');
    } else {
      console.log('Please enter both email and password');
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`🔐 Logging in with ${provider}...\n\nIn production, this would redirect to ${provider} OAuth.`);
    setTimeout(() => {
      setAuthStep('profile');
      setLoginMethod('social');
      if (provider === 'Google') {
        setUserProfile({ ...userProfile, email: 'user@gmail.com' });
      } else {
        setUserProfile({ ...userProfile, email: 'user@facebook.com' });
      }
    }, 1000);
  };

  const handleProfilePicUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUserProfile({ ...userProfile, profilePic: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const searchAddress = (query) => {
    setUserProfile({ ...userProfile, address: query });
    if (query.length < 3) {
      setShowAddressSuggestions(false);
      return;
    }
    const mockSuggestions = [
      { address: `${query}, Sector 29, Gurugram, Haryana 122001`, city: 'Gurugram', pincode: '122001' },
      { address: `${query}, DLF Phase 3, Gurugram, Haryana 122002`, city: 'Gurugram', pincode: '122002' },
      { address: `${query}, Sushant Lok, Gurugram, Haryana 122009`, city: 'Gurugram', pincode: '122009' }
    ];
    setAddressSuggestions(mockSuggestions);
    setShowAddressSuggestions(true);
  };

  const selectAddress = (suggestion) => {
    setUserProfile({
      ...userProfile,
      address: suggestion.address,
      city: suggestion.city,
      pincode: suggestion.pincode
    });
    setShowAddressSuggestions(false);
  };

  const handleProfileSubmit = () => {
    if (userProfile.name && userProfile.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userProfile.email)) {
        console.log('Please enter a valid email address');
        return;
      }  
      const updatedUser = {
        ...userProfile
      };
      apiService.updateUserProfile(updatedUser);
      setIsAuthenticated(true);
      setShowAuthModal(false);
      console.log(`✅ Welcome ${userProfile.name}!\n\nYour profile has been created successfully.`);
      addSecurityLog('Profile Created', `User profile created for ${userProfile.name}`, 'success');
      setAuthStep('completed');
   //   setActiveTab('home');
    } else {
      console.log('Please fill in all required fields (Name and Email)');
    }
  };

  const enableBiometric = () => {
    setBiometricEnabled(true);
    console.log('✅ Biometric login enabled!\n\nYou can now use fingerprint or face recognition to sign in quickly.');
  };

  const handleBiometricLogin = () => {
  setTimeout(() => {
    setIsAuthenticated(true);
    setShowAuthModal(false);
    addSecurityLog('Login Successful', 'Logged in via Biometric Authentication', 'success');
    console.log('✅ Biometric login successful!\n\nWelcome back!');
  }, 1000);
};
  const addSecurityLog = (action, detail, type) => {
    const newLog = {
      id: securityLogs.length + 1,
      action,
      detail,
      time: 'Just now',
      type
    };
    setSecurityLogs([newLog, ...securityLogs]);
  };

const handleLogout = () => {
  if (window.confirm('Are you sure you want to logout?')) {
    // Reset authentication state
    setIsAuthenticated(false);
    setShowAuthModal(true);
    
    // Reset auth flow
    setAuthStep('phone');
    setMobile('');
    setOtp(['', '', '', '', '', '']);
    setPassword('');
    
    // Reset user data
    setUserProfile({
      name: '',
      email: '',
      address: '',
      mobile: '',
      city: '',
      pincode: '',
      profilePic: null
    });
    
    // Reset cart and subscription
    setCart([]);
    setSubscription(null);
    
    // Reset UI state
    setActiveTab('home');
    setSearchQuery('');
    
    // Add security log
    addSecurityLog('Logout', 'User logged out manually', 'info');
    
    // Show success message
    console.log('✅ Logged out successfully!');
  }
};
  const logoutAllDevices = () => {
    if (window.confirm('Are you sure you want to logout from all devices?')) {
      setLoginHistory(loginHistory.map(device => ({ ...device, current: device.current })));
      addSecurityLog('Logout All Devices', 'User logged out from all devices', 'info');
      console.log('✅ Logged out from all devices successfully!');
    }
  };

  const logoutDevice = (deviceId) => {
    setLoginHistory(loginHistory.filter(device => device.id !== deviceId));
    addSecurityLog('Device Logout', `Logged out from device ID: ${deviceId}`, 'info');
    console.log('✅ Device logged out successfully!');
  };

  // Add before return statement (around line 740)
    const handleAddAddress = async (newAddress) => {
      try {
        setLoading(true);
        
        if (editingAddress) {
          // Edit mode - update existing address
          const response = await apiService.updateDeliveryAddress(editingAddress.id, newAddress);
          
          if (response.success) {
            setDeliveryAddresses(deliveryAddresses.map(addr => 
              addr.id === editingAddress.id ? { ...addr, ...newAddress } : addr
            ));
            console.log('✅ Address updated successfully!');
          } else {
            console.log('❌ Failed to update address: ' + (response.data?.message || 'Unknown error'));
          }
        } else {
          // Add mode - create new address
          const address = {            
            ...newAddress,
            isDefault: deliveryAddresses.length === 0
          };
          
          const response = await apiService.addDeliveryAddress(newAddress);
          
          if (response.success) {
            // If new address is default, reset others
            if (address.isDefault) {
              const resetDefaults = deliveryAddresses.map(addr => ({ ...addr, isDefault: false }));
              setDeliveryAddresses([...resetDefaults, address]);
            } else {
              setDeliveryAddresses([...deliveryAddresses, address]);
            }
            console.log('✅ Address added successfully!');
          } else {
            console.log('❌ Failed to add address: ' + (response.data?.message || 'Unknown error'));
          }
        }
        
        setEditingAddress(null); // Reset editing state
        setShowAddAddress(false); // Close modal
      } catch (err) {
        console.error('Error saving address:', err);
        console.log('❌ Error saving address. Please try again.');
      } finally {
        setLoading(false);
      }
    };     

    const handleUpdateAddress = async (id, updatedAddress) => {
      const response = await apiService.updateDeliveryAddress(id, updatedAddress);
      if (!response.success) {
        console.log('❌ Failed to update address: ' + (response.data?.message || 'Unknown error'));
        return;
      }
      setDeliveryAddresses(deliveryAddresses.map(addr => 
        addr.id === id ? { ...addr, ...updatedAddress } : addr
      ));
      console.log('✅ Address updated successfully!');
    };

    const handleDeleteAddress = async (id) => {
      if (deliveryAddresses.length === 1) {
        console.log('⚠️ You must have at least one delivery address');
        return;
      }
      console.log('Attempting to delete address ID: ' + id);
      const response = await apiService.deleteDeliveryAddress(id);
      if (!response.success) {
        console.log('❌ Failed to delete address: ' + (response.data?.message || 'Unknown error'));
        return;
      }
      const addressToDelete = deliveryAddresses.find(addr => addr.id === id);
      
      if (window.confirm(`Delete ${addressToDelete.label} address?`)) {
        const remaining = deliveryAddresses.filter(addr => addr.id !== id);
        
        // If deleted address was default, make first address default
        if (addressToDelete.isDefault && remaining.length > 0) {
          remaining[0].isDefault = true;
        }
        
        setDeliveryAddresses(remaining);
        console.log('✅ Address deleted successfully!');
      }
    };

    const handleSetDefaultAddress = async (id) => {

      const response = await apiService.setDefaultDeliveryAddress(id);
      if (!response.success) {
        console.log('❌ Failed to set default address: ' + (response.data?.message || 'Unknown error'));
        return;
      }
      setDeliveryAddresses(deliveryAddresses.map(addr => ({
        ...addr,
        isDefault: addr.id === id
      })));
      console.log('✅ Default address updated!');
    };
    

    // Add this after line 200 (after existing useEffects)
    useEffect(() => {
      if (isAuthenticated) {
        loadDeliveryAddresses();
      }
    }, [isAuthenticated]);

    const loadDeliveryAddresses = async () => {
      try {
        setLoading(true);
        const response = await apiService.fetchDeliveryAddresses();
        
        if (response.success && response.data.addresses) {
          setDeliveryAddresses(response.data.addresses);
        } else if (deliveryAddresses.length === 0 && userProfile.address) {
          // Fallback: Create address from user profile if no addresses exist
          const defaultAddress = {
            id: 1,
            label: 'Home',
            recipient_name: userProfile.name || 'John Doe',
            address: userProfile.address,
            city: userProfile.city,
            state: 'Haryana',
            country: 'India',
            landmark: 'Near City Mall',
            pincode: userProfile.pincode,
            isDefault: true,
            phone: userProfile.mobile || mobile,
            latitude: 28.4595,
            longitude: 77.0266
          };
          setDeliveryAddresses([defaultAddress]);
        }
      } catch (err) {
        console.error('Failed to load delivery addresses:', err);
      } finally {
        setLoading(false);
      }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(10);
    const [ordersPagination, setOrdersPagination] = useState(null);

    const loadOrders = async (page = currentPage) => {
      try {
        console.log('🔄 Loading orders... page:', page, 'isAuthenticated:', isAuthenticated);
        setLoading(true);
        const result = await orderHelpers.getMyOrders(page, ordersPerPage);
        console.log('📦 Orders result:', result);
        
        if (result && typeof result === 'object') {
          // Handle paginated response
          const ordersData = result.orders || result;
          const paginationData = result.pagination || null;
          
          console.log('📦 Orders count:', ordersData?.length);
          console.log('📄 Pagination:', paginationData);
          
          setOrders(Array.isArray(ordersData) ? ordersData : []);
          setOrdersPagination(paginationData);
          setCurrentPage(page);
        } else {
          // Fallback for non-paginated response
          setOrders(Array.isArray(result) ? result : []);
        }
      } catch (err) {
        console.error('❌ Failed to load orders:', err);
        setOrders([]);
        setOrdersPagination(null);
      } finally {
        setLoading(false);
      }
    };

    const handlePageChange = (newPage) => {
      if (newPage >= 1 && (!ordersPagination || newPage <= ordersPagination.total_pages)) {
        loadOrders(newPage);
      }
    };

    useEffect(() => {
      console.log('📊 Orders useEffect triggered:', { isAuthenticated, activeTab });
      if (isAuthenticated && activeTab === 'orders') {
        // Load orders when user navigates to orders tab
        console.log('✅ Conditions met, calling loadOrders...');
        loadOrders();
      } else {
        console.log('⏭️ Skipping loadOrders:', { isAuthenticated, activeTab });
      }
    }, [isAuthenticated, activeTab]);

    useEffect(() => {
      if (isAuthenticated) {
        // Load cart from backend after authentication
        loadCart();
      }
    }, [isAuthenticated]);
    
    
  // This is a simplified version - full App.js continues with all screens and modals
  // Due to length limits, components are separated in other artifacts
  
  // Calculate filtered products inside component
  const filteredProducts = Array.isArray(productsList) 
    ? productsList.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];
  
  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen relative">     
    

      {isAuthenticated && (
        <>
          {activeTab === 'home' && (
              <HomeScreen
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setShowNotifications={setShowNotificationsModal}
                setShowLocationModal={setShowLocationModal}
                showLocationModal={showLocationModal}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                currentBanner={currentBanner}
                setCurrentBanner={setCurrentBanner}
                addToCart={addToCart}
                setShowSubscription={setShowSubscription}
                products={productsList}
                filteredProducts={filteredProducts}
                cart={cart}
                getCartCount={getCartCount}
                openCheckout={openCheckout}
                userProfile={userProfile}
                notifications={notifications}
                favorites={favorites}
                setShowFavorites={setShowFavorites}
                setActiveTab={setActiveTab}
              />
            )}
          {activeTab === 'cart' && (<CartScreen 
            cart={cart}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            getCartTotal={getCartTotal}
            handleCheckout={openCheckout}
            getCartCount={getCartCount}              
          />)}
          {activeTab === 'orders' && (<OrdersScreen 
            orders={orders}
            loading={loading}
            loadOrders={loadOrders}
            pagination={ordersPagination}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          /> )}
          {activeTab === 'profile' && (<ProfileScreen 
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            handleLogout={handleLogout}
            subscription={subscription}
            setSubscription={setSubscription}
            twoFactorEnabled={twoFactorEnabled}
            setTwoFactorEnabled={setTwoFactorEnabled}
            showSecuritySettings={showSecuritySettings}
            setShowSecuritySettings={setShowSecuritySettings}
            loginHistory={loginHistory}
            securityLogs={securityLogs}
            orders={orders}
            favorites={favorites}
            setFavorites={setFavorites}
            setShowMyOrders={setShowMyOrders}
            setShowFavorites={setShowFavorites}
            setShowNotificationsModal={setShowNotificationsModal}
            setShowPaymentMethods={setShowPaymentMethods}
            setShowEditAddress={setShowEditAddress}
            setShowEditProfile={setShowEditProfile}
            addSecurityLog={addSecurityLog}
            deliveryAddresses={deliveryAddresses}
            handleAddAddress={handleAddAddress}
            handleUpdateAddress={handleUpdateAddress}
            handleDeleteAddress={handleDeleteAddress}
            handleSetDefaultAddress={handleSetDefaultAddress}
            setShowAddAddress={setShowAddAddress} 
            editingAddress={editingAddress}         
            setEditingAddress={setEditingAddress} 
            />)}

          {showCheckout && (<CheckoutModal 
            cart={cart}
            getCartTotal={getCartTotal}
            deliverySlots={deliverySlots}
            selectedSlot={deliverySlot}
            setSelectedSlot={setDeliverySlot}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            onClose={() => setShowCheckout(false)}
            processPayment={processPayment}            
          />)}

          {showPayment && (
            <PaymentModal
              isOpen={showPayment}
              onClose={() => {
                setShowPayment(false);
                setShowCheckout(true);
              }}
              paymentMethod={paymentMethod}
              totalAmount={getCartTotal()}
              onPaymentComplete={handlePaymentComplete}
            />
          )}

          {showOrderConfirmation && (
            <OrderConfirmationModal
              isOpen={showOrderConfirmation}
              orderDetails={orderDetails}
              onViewOrders={() => {
                setShowOrderConfirmation(false);
                setActiveTab('orders');
                setShowCheckout(false);
              }}
              onContinueShopping={() => {
                setShowOrderConfirmation(false);
                setShowCheckout(false);
                setActiveTab('home');
              }}
            />
          )}

        {showSubscription && <SubscriptionModal 
          isOpen={!!showSubscription}
          product={showSubscription}
          plans={subscriptionPlans}
          onClose={() => setShowSubscription(null)}
          subscriptionPlans={subscriptionPlans}
          showSubscription={showSubscription}
          subscribeProduct={subscribeProduct}
        />}
          {showLocationModal && (<LocationModal 
            locations={locations}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            onClose={() => setShowLocationModal(false)}
          />)}

          {showMyOrders && (<MyOrdersModal
            isOpen={showMyOrders}
            onClose={() => setShowMyOrders(false)}
            orders={orders}
          />)}

          {showFavorites && (<FavoritesModal
            isOpen={showFavorites}
            onClose={() => setShowFavorites(false)}
            favorites={favorites}
            setFavorites={setFavorites}
            addToCart={addToCart}
          />)}

          {showNotificationsModal && (<NotificationsModal
            isOpen={showNotificationsModal}
            onClose={() => setShowNotificationsModal(false)}
            notifications={notifications}
            setNotifications={setNotifications}
          />)}

          {showPaymentMethods && (
            <PaymentMethodsModal
              isOpen={showPaymentMethods}
              onClose={() => setShowPaymentMethods(false)}
              paymentMethods={paymentMethods}
              setPaymentMethods={setPaymentMethods}                
            />
          )}

          {showSecuritySettings && (
            <SecuritySettingsModal
              setShowSecuritySettings={setShowSecuritySettings}
              sessionTimeout={sessionTimeout}
              setSessionTimeout={setSessionTimeout}
              loginHistory={loginHistory}
              logoutAllDevices={logoutAllDevices}
              logoutDevice={logoutDevice}
              securityLogs={securityLogs}
            />
          )}

          {showEditAddress && (
            <EditAddressModal
              isOpen={showEditAddress}
              onClose={() => setShowEditAddress(false)}
              userProfile={userProfile}
              setUserProfile={setUserProfile}
              searchAddress={searchAddress}
              addressSuggestions={addressSuggestions}
              showAddressSuggestions={showAddressSuggestions}
              selectAddress={selectAddress}
            />
          )}
          {showEditProfile && (
          <EditProfileModal
            isOpen={showEditProfile}
            onClose={() => setShowEditProfile(false)}
            userProfile={userProfile}
            setUserProfile={setUserProfile}
            handleProfilePicUpload={handleProfilePicUpload}
          />
        )}
        {showAddAddress && (
          <AddAddressModal
            isOpen={showAddAddress}
            onClose={() => {
              setShowAddAddress(false);
              setEditingAddress(null); // Reset on close
            }}
            onSave={handleAddAddress}
            editAddress={editingAddress} // Pass the address to edit
          />
        )}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-w-md mx-auto">
            <div className="flex justify-around py-3">
              <button
                onClick={() => setActiveTab('home')}
                className={`flex flex-col items-center ${activeTab === 'home' ? 'text-blue-600' : 'text-gray-400'}`}
              >
                <Home size={24} />
                <span className="text-xs mt-1">Home</span>
              </button>

              <button
                onClick={() => setActiveTab('cart')}
                className={`flex flex-col items-center relative ${activeTab === 'cart' ? 'text-blue-600' : 'text-gray-400'}`}
              >
                <ShoppingCart size={24} />
                {cart.length > 0 && getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
                <span className="text-xs mt-1">Cart</span>
              </button>

              <button
                onClick={() => setActiveTab('orders')}
                className={`flex flex-col items-center ${activeTab === 'orders' ? 'text-blue-600' : 'text-gray-400'}`}
              >
                <Package size={24} />
                <span className="text-xs mt-1">Orders</span>
              </button>

              <button
                onClick={() => setActiveTab('profile')}
                className={`flex flex-col items-center ${activeTab === 'profile' ? 'text-blue-600' : 'text-gray-400'}`}
              >
                <User size={24} />
                <span className="text-xs mt-1">Profile</span>
              </button>
            </div>
          </div>
        </>
      )}
       {!showForgotPassword && !showSignUp && (
       <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          authStep={authStep}
          setAuthStep={setAuthStep}
          loginMethod={loginMethod}
          setLoginMethod={setLoginMethod}
          mobile={mobile}
          setMobile={setMobile}
          whatsAppNumber={whatsAppNumber}
          setWhatsAppNumber={setWhatsAppNumber}
          sendOTP={sendOTP}
         // sendOTPForLogin={sendOTPForLogin}
          sendOTPForLogin={sendOTP}
          otp={otp}
          handleOtpChange={handleOtpChange}
          handleOtpKeyDown={handleOtpKeyDown}
          otpMethod={otpMethod}
          timer={timer}
          canResend={canResend}
          resendOTP={resendOTP}
          verifyOTP={verifyOTP}
          password={password}
          checkPasswordStrength={checkPasswordStrength}
          passwordStrength={passwordStrength}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleEmailLogin={handleEmailLogin}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}
          handleSocialLogin={handleSocialLogin}
          biometricEnabled={biometricEnabled}
          handleBiometricLogin={handleBiometricLogin}
          connectWhatsApp={connectWhatsApp}
          skipWhatsApp={skipWhatsApp}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          handleProfilePicUpload={handleProfilePicUpload}
          searchAddress={searchAddress}
          addressSuggestions={addressSuggestions}
          showAddressSuggestions={showAddressSuggestions}
          selectAddress={selectAddress}
          setShowTwoFactorSetup={setShowTwoFactorSetup}
          twoFactorEnabled={twoFactorEnabled}
          biometricAvailable={biometricAvailable}
          enableBiometric={enableBiometric}
          handleProfileSubmit={handleProfileSubmit}
          showForgotPassword={() => setShowForgotPassword(true)}
          showSignUp={() => setShowSignUp(true)}
        />
      )}

      {showSignUp && (
        <SignUpModal 
          isOpen={showSignUp}
          onClose={() => setShowSignUp(false)}
          onBackToLogin={() => setShowSignUp(false)}
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          authStep={authStep}
          setAuthStep={setAuthStep}
        />
      )}

      {showForgotPassword && (
        <ForgotPasswordModal 
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          authStep={authStep}
          setAuthStep={setAuthStep}
          onBackToLogin={() => setShowForgotPassword(false)}
        />
      )}
    </div>
  );
};

export default MilkEcommerceApp;